import { Anchor, Box, Text, ThemeContext } from 'grommet';
import React, { useContext, useEffect, useState } from 'react';
import { DarkenOnHoverBox } from '../../../utils/styled';
import Icon from '../../Icon';
import GenericTagInput from '../../GenericTagInput';
import { IconX as IconClose } from '@tabler/icons-react';
import { formatName } from '../../../utils/names';
import { validateEmail } from '../../../lib/validation';
import StatusIcon from '../../StatusIcon';
import Button from '../../Button';

const ONE_LINE = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const EmailTagInput = ({
    onEmailChange, 
    onKeywordChange, 
    setHasEmailValidationError, 
    value, 
    setInlineError,
    team,
    onSubmit
}) => {
    const [selectedTags, setSelectedTags] = useState(value || []);
    const [keyword, setKeyword] = useState('');
    const theme = useContext(ThemeContext);

    useEffect(() => {
        setInlineError();
        onEmailChange && onEmailChange({value: selectedTags}); // when user chooses a customer from suggestion
        setHasEmailValidationError(false); // check that all emails are valid
        selectedTags && selectedTags.map(tag => typeof validateEmail(tag?.email) !== "undefined" && setHasEmailValidationError(true));
    }, [selectedTags]);

    useEffect(() => {
        !keyword && setInlineError();
    }, [keyword]);

    const renderSuggestions = () => {
        if (!keyword || !keyword.trim()) return []; // don't show suggestions if user hasn't typed in a search string yet
        return (team?.users || [])
            .filter(user => {
                const name = formatName(user, true).toLowerCase();
                const email = user?.email.toLowerCase();
                const searchStr = keyword.toLowerCase(); // don't need to strip spaces
                return email.includes(searchStr) || name.includes(searchStr)
            })
            .filter(user => !(selectedTags|| []).find(chosenUser => chosenUser.email === user.email))
            .map(user => ({
                label: (
                    <Box pad={{vertical: "xsmall", horizontal: "small"}}>
                        <Text weight="bold">{formatName(user, true)}</Text>
                        <Text size="small">{user?.email}</Text>
                    </Box>
                ),
                value: user
            }))
    };

    const onRemoveTag = (email) => {
        const removeIndex = selectedTags.findIndex(u => u.email === email);
        const newTags = [...selectedTags];
        if (removeIndex >= 0) {
            newTags.splice(removeIndex, 1);
        }
        setSelectedTags(newTags);
    };

    const onAddTag = (tag) => {
        if (!keyword || !keyword.trim()) { // prevent adding tag that is just white space
            setKeyword('');
            onKeywordChange && onKeywordChange('');
            return;
        }
        if ((selectedTags || []).map(tag => tag?.email).includes(keyword.trim())) {
            setInlineError("You've already entered this email");
            return;
        }
        const newTags = [...selectedTags, tag];
        setSelectedTags(newTags);
        setKeyword('');
        onKeywordChange && onKeywordChange('');
    }

    return (
        <Box pad='xsmall'>
            <GenericTagInput
                showSearchIcon={false}
                placeholder={!selectedTags || selectedTags.length === 0 ? "Enter email address" : ""}
                suggestions={renderSuggestions()}
                value={selectedTags}
                onRemove={onRemoveTag}
                onAdd={onAddTag}
                onChange={({ target: { value } }) => {
                    setKeyword(value);
                    onKeywordChange && onKeywordChange(value);
                }}
                inputText={keyword}
                onKeyDown={e => {
                    // Grommet has already taken an Enter that picks a highlighted suggestion
                    if (e.key === "Enter" && !e.defaultPrevented) {
                        e.preventDefault();
                        onSubmit && onSubmit();
                        return;
                    }
                    // In an empty input, Backspace takes the last tag back off
                    if (e.key === "Backspace" && !keyword && selectedTags.length) {
                        onRemoveTag(selectedTags[selectedTags.length - 1].email);
                        return;
                    }
                    if (e.code !== "Space") return;
                    // The tag takes the text, so the space must not land in the emptied input
                    if (keyword && keyword.trim()) e.preventDefault();
                    onAddTag({email: keyword.trim()});
                }}
                renderValue={(vals, onRemove) =>
                    vals.map((user, index) => {
                        const isValidEmail = typeof validateEmail(user?.email.trim()) == "undefined";
                        return (
                            <Box margin="xxsmall" key={index}>
                                <Button
                                    event={{section: 'settings_email_notification_section', element: 'email_tag_input'}}
                                    label={(
                                        <DarkenOnHoverBox
                                            background={isValidEmail ? (theme.dark ? "foreground" : "light-1") : {color: "status-critical", opacity: theme.dark ? '0.2' : '0.1'}}
                                            border={ !isValidEmail ? undefined : {color: "border"}}
                                            round="xxsmall"
                                            theme={theme}>
                                            <Box direction="row" align="center" pad={{vertical: "xxsmall", horizontal: 'xsmall'}} gap="xsmall">
                                                {!isValidEmail && (
                                                    <Box margin={{vertical: 'xsmall'}}>
                                                        <StatusIcon type={"error"} size="xsmall"/>
                                                    </Box>
                                                )}
                                                {/* A teammate by name, a typed address by the address, on one line */}
                                                <Text style={{ ...ONE_LINE, minWidth: 0 }}>{formatName(user, true) || user.email}</Text>
                                                {onRemove && (
                                                    <Box pad={{right: 'xsmall'}}>
                                                        <Anchor
                                                            plain
                                                            hoverIndicator
                                                            label={(<Icon SelectedIcon={IconClose} color="text-weak" size="xxxsmall" />)}
                                                            a11yTitle={`Remove ${user?.email}`}
                                                            onClick={() => onRemove(user?.email)} />
                                                    </Box>
                                                )}
                                            </Box>
                                        </DarkenOnHoverBox>
                                    )}
                                    plain
                                    hoverIndicator/>
                            </Box>
                        )
                    })
                }
            />
        </Box>
    );
};

export default EmailTagInput;
